import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import i18n from '../i18n'
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  SUPPORTED,
  resolveLanguage,
} from '../i18n/languages'
import { storageGet, storageSet } from '../utils/safeStorage'

const STORAGE_KEY = 'automais-language'

const LanguageContext = createContext(null)

function detectInitialLanguage() {
  const stored = resolveLanguage(storageGet(STORAGE_KEY))
  if (stored) return stored
  if (typeof navigator !== 'undefined') {
    const candidates = []
    if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages)
    if (navigator.language) candidates.push(navigator.language)
    for (const candidate of candidates) {
      const resolved = resolveLanguage(candidate)
      if (resolved) return resolved
    }
  }
  return DEFAULT_LANGUAGE
}

function applyHtmlLang(code) {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = code
  }
}

export function LanguageProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState(() => detectInitialLanguage())

  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage)
    }
    applyHtmlLang(currentLanguage)
  }, [currentLanguage])

  useEffect(() => {
    const handler = (code) => {
      const resolved = resolveLanguage(code) || DEFAULT_LANGUAGE
      setCurrentLanguage((prev) => (prev === resolved ? prev : resolved))
    }
    i18n.on('languageChanged', handler)
    return () => i18n.off('languageChanged', handler)
  }, [])

  const setLanguage = (code) => {
    const resolved = resolveLanguage(code)
    if (!resolved) return
    storageSet(STORAGE_KEY, resolved)
    setCurrentLanguage(resolved)
  }

  const value = useMemo(
    () => ({
      currentLanguage,
      languages: LANGUAGES,
      supported: SUPPORTED,
      setLanguage,
    }),
    [currentLanguage],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage deve ser usado dentro de LanguageProvider')
  }
  return context
}
